import React, { useState } from 'react';
import './EmailSender.css';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import emailjs from '@emailjs/browser';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const EmailSender = ({ setRoute }) => {
  const [subjectPrefix, setSubjectPrefix] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const removeFile = (fileToRemove) => {
    setSelectedFiles(selectedFiles.filter((file) => file !== fileToRemove));
  };

  const calculateTotalSize = (files) => {
    return files.reduce((acc, file) => acc + file.size, 0);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    setMessageType('');

    if (!subjectPrefix) {
      setMessage('El prefijo del asunto es obligatorio.');
      setMessageType('error');
      return;
    }

    if (selectedFiles.length === 0) {
      setMessage('Por favor, selecciona al menos un archivo.');
      setMessageType('error');
      return;
    }

    if (selectedFiles.length > 10) {
      setMessage('Máximo 10 archivos permitidos.');
      setMessageType('error');
      return;
    }

    const totalSize = calculateTotalSize(selectedFiles);
    if (totalSize > 100 * 1024 * 1024) { // 100MB
      setMessage('El tamaño total de los archivos no debe superar los 100MB.');
      setMessageType('error');
      return;
    }

    setIsUploading(true);
    setMessage('Subiendo archivos...');
    setMessageType('info');

    try {
      const uploadedFilesInfo = [];

      // 1. Upload files to Firebase Storage
      for (const file of selectedFiles) {
        const storageRef = ref(storage, `${subjectPrefix}/${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        uploadedFilesInfo.push({ name: file.name, url });
      }

      // 2. Prepare EmailJS parameters
      const fileListString = uploadedFilesInfo
        .map((f) => `- ${f.name}: ${f.url}`)
        .join('\n');

      const templateParams = {
        subject: subjectPrefix,
        file_list: fileListString,
      };

      // 3. Send Email
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      setMessage('Archivos subidos y correo enviado correctamente.');
      setMessageType('success');
      setSelectedFiles([]);
      setSubjectPrefix('');
    } catch (error) {
      console.error('Error:', error);
      setMessage('Hubo un error al subir los archivos o enviar el correo.');
      setMessageType('error');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="email-sender-container">
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="subjectPrefix">Asunto:</label>
          <input
            type="text"
            id="subjectPrefix"
            value={subjectPrefix}
            onChange={(e) => setSubjectPrefix(e.target.value)}
            required
            className="form-control"
            placeholder="Introduce el asunto"
            disabled={isUploading}
          />
        </div>

        <div
          className="drop-area"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          role="button"
          style={{ opacity: isUploading ? 0.5 : 1 }}
        >
          <p className="drop-text">Arrastra y suelta los archivos aquí (Max 10 archivos, 100MB total)</p>
          <input
            type="file"
            id="file-input"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
            disabled={isUploading}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
          />
          <label htmlFor="file-input" className="select-button">Seleccionar archivos</label>
        </div>

        <div className="selected-files">
          {selectedFiles.length > 0 && (
            <ul>
              {selectedFiles.map((file, index) => (
                <li key={index} className="file-item">
                  <span className="file-info">{file.name}</span>
                  <span className="file-size">{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                  <button
                    type="button"
                    onClick={() => removeFile(file)}
                    className="remove-file-button"
                    disabled={isUploading}
                  >
                    x
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {selectedFiles.length > 0 && (
          <button type="submit" className="submit-button" disabled={isUploading}>
            {isUploading ? 'Procesando...' : 'Subir Archivos'}
          </button>
        )}
      </form>

      {message && (
        <p className={`notification ${messageType}`}>
          {message}
        </p>
      )}
    </div>
  );
};

export default EmailSender;
